import { ExcelLikeGrid } from "../../../../../shared/excel-grid/components/ExcelLikeGrid";
import { ExcelGridColumn, ExcelGridRow } from "../../../../../shared/excel-grid/models/excel-grid.models";
import {
  ADMIN_CERTIFICATES_SETTINGS_DATA_COLUMNS,
  AdminCertificatesSettingsRow,
} from "../../../../models/admin-certificates-settings.models";
import { formatBooleanValue } from "../../../../utils/admin-certificates-settings.utils";
import {
  certificatesSettingsValueClass,
  formatCellValue,
  noopCellCommit,
} from "../helpers/admin-certificates-settings-grid.helpers";
import { LoadingState } from "../helpers/admin-certificates-settings-panel.types";
import CenteredCell from "./CenteredCell";
import CertificateNameCell from "./CertificateNameCell";
import EditRowButton from "./EditRowButton";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  rows: AdminCertificatesSettingsRow[];
  loadingState: LoadingState;
  onEditRow: (row: AdminCertificatesSettingsRow) => void;
};

const AdminCertificatesSettingsGrid = ({ rows, loadingState, onEditRow }: Props) => {
  const columns = useMemo<ExcelGridColumn[]>(
    () => [
      ...ADMIN_CERTIFICATES_SETTINGS_DATA_COLUMNS.map((column) =>
        column.key === "CERTIFICATE_NAME"
          ? {
              ...column,
              renderCell: (row: ExcelGridRow) => (
                <CertificateNameCell>{formatCellValue(row.CERTIFICATE_NAME)}</CertificateNameCell>
              ),
            }
          : column.key === "ID"
            ? {
                ...column,
                renderCell: (row: ExcelGridRow) => (
                  <CenteredCell className={certificatesSettingsValueClass}>{formatCellValue(row.ID)}</CenteredCell>
                ),
              }
            : column.key === "KPI_INCLUDED"
              ? {
                  ...column,
                  renderCell: (row: ExcelGridRow) => (
                    <CenteredCell className={certificatesSettingsValueClass}>
                      {formatBooleanValue(row.KPI_INCLUDED)}
                    </CenteredCell>
                  ),
                }
              : column.key === "EXPIRY_NOTICE_DAYS" || column.key === "ENDORSEMENT_NOTICE_DAYS"
                ? {
                    ...column,
                    renderCell: (row: ExcelGridRow) => (
                      <CenteredCell className={certificatesSettingsValueClass}>
                        {formatCellValue(row[column.key])}
                      </CenteredCell>
                    ),
                  }
                : column.key === "EXCLUDED_VESSEL_TYPES"
                  ? {
                      ...column,
                      renderCell: (row: ExcelGridRow) => (
                        <TruncatedCell value={row.EXCLUDED_VESSEL_TYPES} className={certificatesSettingsValueClass} />
                      ),
                    }
                  : column.key === "UPDATED_BY"
                    ? {
                        ...column,
                        renderCell: (row: ExcelGridRow) => <TruncatedCell value={row.UPDATED_BY} />,
                      }
                    : {
                        ...column,
                        renderCell: (row: ExcelGridRow) => (
                          <CenteredCell>{formatCellValue(row[column.key])}</CenteredCell>
                        ),
                      },
      ),
      {
        key: "__actions",
        label: "Edit",
        editable: false,
        filterable: false,
        sortable: false,
        minWidth: 44,
        maxWidth: 48,
        renderCell: (row) => <EditRowButton row={row as AdminCertificatesSettingsRow} onEditRow={onEditRow} />,
      },
    ],
    [onEditRow],
  );

  if (loadingState === "loading") {
    return <div className="min-h-0 flex-1" data-test="admin-certificates-settings-grid-loading-boundary" />;
  }

  return (
    <ExcelLikeGrid
      rows={rows}
      columns={columns}
      dataTest="admin-certificates-settings-grid"
      onCellCommit={noopCellCommit}
      showHeadersWhenEmpty
      rowHeight={48}
    />
  );
};

const TruncatedCell = ({ value, className }: { value: unknown; className?: string }) => {
  const text = formatCellValue(value);

  return (
    <div
      title={text || undefined}
      aria-label={text || undefined}
      className="flex h-full min-h-10 min-w-0 items-center justify-center text-center"
    >
      <span className={twMerge("block min-w-0 max-w-full truncate", className)}>{text}</span>
    </div>
  );
};

export default AdminCertificatesSettingsGrid;
